import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Phone, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cx } from '@/lib/format';
import { useStore } from '@/context/StoreContext';
import { googleSignIn, appleSignIn } from '@/lib/socialAuth';

export default function Auth() {
  const [tab, setTab] = useState('login');
  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const { login, register, loginWithToken } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === 'login') await login(form.email, form.password);
      else await register(form);
      navigate('/account');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const socialLogin = async (provider) => {
    try {
      const token = provider === 'google' ? await googleSignIn() : await appleSignIn();
      const { data } = await api.post(`/auth/${provider}`, { token });
      loginWithToken(data.access_token, data.user);
      toast.success('Logged in!');
      navigate('/account');
    } catch (e) {
      if (e?.message === 'not_configured') toast.error(`${provider === 'google' ? 'Google' : 'Apple'} Sign-In isn't configured yet`);
      else toast.error(e?.response?.data?.detail || 'Sign-in failed');
    }
  };

  const sendOtp = async () => {
    if (phone.length < 10) { toast.error('Enter a valid phone number'); return; }
    try {
      const { data } = await api.post('/auth/otp/request', { phone });
      setOtpSent(true);
      setCooldown(data.resend_in || 30);
      toast.success(data.dev_otp ? `OTP sent (demo: ${data.dev_otp})` : 'OTP sent to your phone');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send OTP'); }
  };
  const verifyOtp = async () => {
    try { const { data } = await api.post('/auth/otp/verify', { phone, otp }); loginWithToken(data.access_token, data.user); toast.success('Logged in!'); navigate('/account'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Invalid OTP'); }
  };
  const forgot = async () => {
    if (!form.email) { toast.error('Enter your email first'); return; }
    try { const { data } = await api.post('/auth/forgot-password', { email: form.email }); toast.success(`Reset link sent${data.dev_token ? ' (demo mode)' : ''}`); }
    catch { toast.error('Could not process request'); }
  };

  const field = 'w-full rounded-lg border border-border bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';

  return (
    <div className="container-gaze grid min-h-[70vh] place-items-center py-10">
      <div className="w-full max-w-md">
        <div className="text-center"><Link to="/" className="font-display text-3xl">Gaze Decor</Link><p className="mt-1 text-sm text-muted-foreground">Welcome to luxury living</p></div>
        <div className="mt-6 rounded-3xl border border-border bg-white p-6 sm:p-8">
          <div className="mb-6 grid grid-cols-2 rounded-full bg-[hsl(var(--gaze-ivory))] p-1">
            <button onClick={() => { setTab('login'); setOtpMode(false); }} className={cx('rounded-full py-2 text-sm font-semibold', tab === 'login' && !otpMode ? 'bg-white shadow' : 'text-muted-foreground')} data-testid="auth-login-tab">Login</button>
            <button onClick={() => { setTab('signup'); setOtpMode(false); }} className={cx('rounded-full py-2 text-sm font-semibold', tab === 'signup' ? 'bg-white shadow' : 'text-muted-foreground')} data-testid="auth-signup-tab">Sign Up</button>
          </div>

          {otpMode ? (
            <div className="space-y-3">
              <div className="relative"><Phone size={16} className="absolute left-3 top-3 text-muted-foreground" /><input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} maxLength={10} placeholder="Phone number" className={field} data-testid="otp-phone-input" /></div>
              {otpSent && <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} maxLength={6} placeholder="Enter 6-digit OTP" className="w-full rounded-lg border border-border py-2.5 px-3 text-center text-lg tracking-[0.5em] outline-none" data-testid="otp-code-input" />}
              <button onClick={otpSent ? verifyOtp : sendOtp} disabled={!otpSent && cooldown > 0} className="w-full rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white disabled:opacity-60" data-testid="otp-action-btn">{otpSent ? 'Verify & Login' : cooldown > 0 ? `Send OTP in ${cooldown}s` : 'Send OTP'}</button>
              {otpSent && (
                <button onClick={sendOtp} disabled={cooldown > 0} className="w-full text-center text-xs text-[hsl(var(--primary))] disabled:opacity-50" data-testid="otp-resend-btn">{cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'}</button>
              )}
              <button onClick={() => { setOtpMode(false); setOtpSent(false); setOtp(''); }} className="w-full text-center text-xs text-muted-foreground">Use email instead</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {tab === 'signup' && <div className="relative"><User size={16} className="absolute left-3 top-3 text-muted-foreground" /><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={field} data-testid="auth-name" required /></div>}
              <div className="relative"><Mail size={16} className="absolute left-3 top-3 text-muted-foreground" /><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email address" className={field} data-testid="auth-email" required /></div>
              {tab === 'signup' && <div className="relative"><Phone size={16} className="absolute left-3 top-3 text-muted-foreground" /><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={field} /></div>}
              <div className="relative"><Lock size={16} className="absolute left-3 top-3 text-muted-foreground" /><input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} type="password" placeholder="Password" className={field} data-testid="auth-password" required /></div>
              {tab === 'login' && <button type="button" onClick={forgot} className="block text-right text-xs text-[hsl(var(--primary))]">Forgot password?</button>}
              <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Please wait...' : tab === 'login' ? 'Login' : 'Create Account'}</button>
            </form>
          )}

          {!otpMode && (
            <>
              <button onClick={() => setOtpMode(true)} className="hidden mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium"><Smartphone size={16} /> Continue with OTP</button>

              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or continue with</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button type="button" onClick={() => socialLogin('google')} className="flex items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium hover:bg-[hsl(var(--gaze-ivory))]" data-testid="auth-google-btn">
                  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.7-4.9-12.4-11.2C11.5 17.9 16.9 12.5 24 12.5c3.1 0 5.9 1.1 8.1 2.9l6-6C34.6 5.9 29.6 4 24 4 13 4 4 13 4 24s9 20 20 20c10.5 0 19.1-8.1 20-18.5.1-.7.1-1.3.1-2 0-1-.1-2-.4-3z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 12.5 24 12.5c3.1 0 5.9 1.1 8.1 2.9l6-6C34.6 5.9 29.6 4 24 4c-7.5 0-14 4.2-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6C29.6 35 26.9 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.9 39.6 16.4 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.4l6.6 5.6C41.4 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
                  Google
                </button>
                <button type="button" onClick={() => socialLogin('apple')} className="hidden flex items-center justify-center gap-2 rounded-full border border-border py-3 text-sm font-medium hover:bg-[hsl(var(--gaze-ivory))]" data-testid="auth-apple-btn">
                  <svg width="16" height="16" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 80.6c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.3zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                  Apple
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
